import { useEffect, useState } from "react";
import api, { formatApiError, uploadFileChunked } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, ImageIcon } from "lucide-react";
import AiImageButton from "@/components/AiImageButton";

export const EMPTY_DEAL = { name: "", reference: "", headline: "", buyer: "", sector: "", department: "", region: "", description: "", image: "", visible: true, business_id: null };

export const dealFromBusiness = (b) => ({
  ...EMPTY_DEAL, business_id: b.business_id, name: b.title || "", reference: b.reference || "", sector: b.sector || "",
  region: b.region || "", headline: `${b.title || ""} cédée`, description: b.short_description || "", image: b.hero_image || "",
});

const Field = ({ label, children }) => <div><Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label><div className="mt-1.5">{children}</div></div>;

const ImageField = ({ value, onChange, deal }) => {
  const [progress, setProgress] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choisissez un fichier image.");
    try { setProgress(0); onChange(await uploadFileChunked(file, setProgress)); toast.success("Image importée"); }
    catch { toast.error("Échec de l'import de l'image"); } finally { setProgress(null); e.target.value = ""; }
  };
  return (
    <div className="grid sm:grid-cols-[160px_1fr] gap-4 items-start">
      <div className="aspect-[3/2] bg-[#F8F6F0] border border-gray-200 overflow-hidden flex items-center justify-center">
        {value ? <img data-testid="deal-image-preview" src={value} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-gray-300" />}
      </div>
      <div className="space-y-2">
        <label data-testid="deal-image-upload" className="inline-flex items-center gap-2 cursor-pointer bg-[#1B2A4A] hover:bg-[#2a3d63] text-white text-sm px-4 py-2 rounded-full transition-colors">
          <Upload className="w-4 h-4" /> {progress != null ? `Import… ${progress}%` : "Importer depuis l'ordinateur"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={progress != null} />
        </label>
        <AiImageButton testid="deal-image-ai" title={deal.name} sector={deal.sector} description={deal.description} onGenerated={onChange} />
        <Input data-testid="deal-field-image" value={value} onChange={(e) => onChange(e.target.value)} placeholder="… ou collez l'URL d'une image" className="rounded-none border-gray-300" />
      </div>
    </div>
  );
};

export default function SoldDealDialog({ deal, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_DEAL);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (deal) setForm({ ...EMPTY_DEAL, ...deal }); }, [deal]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const editing = !!deal?.deal_id;

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = Object.fromEntries(Object.keys(EMPTY_DEAL).map((k) => [k, form[k]]));
    try {
      if (editing) await api.put(`/admin/sold-deals/${deal.deal_id}`, payload);
      else await api.post("/admin/sold-deals", payload);
      toast.success(editing ? "Cession mise à jour" : "Cession ajoutée"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!deal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="sold-deal-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-[#1B2A4A]">{editing ? "Modifier la cession" : "Nouvelle cession réalisée"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_140px] gap-4">
            <Field label="Nom de l'entreprise *"><Input data-testid="deal-field-name" required minLength={2} value={form.name} onChange={set("name")} className="rounded-none border-gray-300" /></Field>
            <Field label="Référence"><Input data-testid="deal-field-reference" value={form.reference} onChange={set("reference")} className="rounded-none border-gray-300" /></Field>
          </div>
          <Field label="Titre affiché"><Input data-testid="deal-field-headline" value={form.headline} onChange={set("headline")} placeholder="Ex. SAS X cédée au groupe Y" className="rounded-none border-gray-300" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Repreneur"><Input data-testid="deal-field-buyer" value={form.buyer} onChange={set("buyer")} className="rounded-none border-gray-300" /></Field>
            <Field label="Secteur"><Input data-testid="deal-field-sector" value={form.sector} onChange={set("sector")} className="rounded-none border-gray-300" /></Field>
            <Field label="Région"><Input data-testid="deal-field-region" value={form.region} onChange={set("region")} className="rounded-none border-gray-300" /></Field>
            <Field label="Département (n°)"><Input data-testid="deal-field-department" maxLength={10} value={form.department} onChange={set("department")} className="rounded-none border-gray-300" /></Field>
          </div>
          <Field label="Description"><Textarea data-testid="deal-field-description" value={form.description} onChange={set("description")} className="rounded-none border-gray-300 min-h-[110px]" /></Field>
          <Field label="Image"><ImageField deal={form} value={form.image} onChange={(image) => setForm((f) => ({ ...f, image }))} /></Field>
          <div className="flex items-center gap-3 bg-[#F8F6F0] p-3">
            <Switch data-testid="deal-field-visible" checked={form.visible} onCheckedChange={(visible) => setForm({ ...form, visible })} />
            <span className="text-sm text-[#1B2A4A]">{form.visible ? "Visible sur la page « Nos dernières cessions »" : "Masquée du site public"}</span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
            <Button data-testid="deal-save" type="submit" disabled={busy} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-6">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
